/**
 * @fileoverview Input/output schemas for the template-generator-v2 agent.
 *
 * This agent generates author or universe style templates based on a name.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Flow Input Schema - what the flow receives.
 * Includes execution fields (logId, model, customPrompt) from BaseFlowInputSchema.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  templateType: z.enum(['author', 'universe']).describe('The type of template to generate.'),
  name: z.string().min(1).describe('The name of the author or universe.'),
});

export type Input = z.infer<typeof InputSchema>;

/**
 * Hook Input type - domain-only fields for hooks using executeAgent.
 * Derived from InputSchema by omitting execution layer fields.
 */
export type HookInput = Omit<Input, 'logId' | 'model' | 'customPrompt'>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output Schema - the generated template code.
 */
export const OutputSchema = z.object({
  templateCode: z.string().describe('The complete TypeScript template code ready for copy-paste.'),
  templateId: z.string().describe('The suggested template ID (kebab-case).'),
  displayName: z.string().describe('The display name for the template.'),
  tags: z.array(z.string()).describe('Suggested tags for the template.'),
  description: z.string().optional().describe('A brief description of the template (for universe templates).'),
});

export type Output = z.infer<typeof OutputSchema>;
